import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { GameplayTierStars } from '@sorare/core/src/components/card/GameplayTierStars';
import { CardScore } from '@sorare/core/src/components/scoring/CardScore';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { getCardSpecialEditionBonus } from '@sorare/core/src/lib/bonus';
import { isType } from '@sorare/core/src/lib/gql';

import { CardBonus } from '#src/components/card/CardBonus/index.tsx';
import { CardSpecialEditionBonus } from '#src/components/card/CardSpecialEditionBonus/index.tsx';
import { ItemEligibility } from '#src/components/card/ItemEligibility/index.tsx';
import { U23Eligible } from '#src/components/card/U23Eligible/index.tsx';
import { InjuryTag } from '#src/components/player/InjuryTag/index.tsx';
import { TokenStatsIcon } from '#src/components/token/TokenStatsIcon/index.tsx';

import type {
  CardPropertiesWithStats_anyCard,
  CardProperties_anyCard,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ gap: 0 })`
  color: var(--c-white);
  flex-wrap: wrap;
  gap: var(--half-unit);
`;

type Props = {
  card: CardProperties_anyCard;
  withTransferMalus: boolean;
  onInfoClick?: () => void;
  cardWithStats?: CardPropertiesWithStats_anyCard;
  playerScoreSmall?: boolean;
};

export const CardProperties = ({
  card,
  withTransferMalus,
  onInfoClick,
  cardWithStats,
  playerScoreSmall,
}: Props) => {
  const { sport } = useSportContext();

  const modePerSport: Record<Sport, TotalAveragePlayerScore> = {
    [Sport.FOOTBALL]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.NBA]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];
  const { anyPlayer, rarityTyped } = card;

  const specialEditionBonus = getCardSpecialEditionBonus(card);

  return (
    <Root>
      {!!cardWithStats && (
        <TokenStatsIcon card={cardWithStats} onInfoClick={onInfoClick} />
      )}
      <CardScore small={playerScoreSmall} card={card} mode={mode} />
      <CardBonus
        card={card}
        withTransferMalus={withTransferMalus}
        size="large"
      />
      {!!specialEditionBonus && (
        <CardSpecialEditionBonus {...specialEditionBonus} />
      )}
      {isType(card, 'Card') && (
        <>
          {!!card.u23Eligible && !specialEditionBonus && (
            <U23Eligible until={card.u23EligibleUntil} />
          )}
          <ItemEligibility cards={[card]} />
        </>
      )}
      <InjuryTag player={anyPlayer} size="small" />
      {rarityTyped === Rarity.common && !!anyPlayer.gameplayTier && (
        <GameplayTierStars gameplayTier={anyPlayer.gameplayTier} />
      )}
    </Root>
  );
};

CardProperties.fragments = {
  anyCard: gql`
    fragment CardProperties_anyCard on AnyCardInterface {
      slug
      rarityTyped
      ... on Card {
        slug
        u23Eligible
        u23EligibleUntil
        ...ItemEligibility_card
      }
      anyPlayer {
        slug
        gameplayTier
        ...InjuryTag_anyPlayer
      }
      ...CardBonus_anyCard
      ...CardScore_anyCard
      ...getCardSpecialEditionBonus_anyCardInterface
    }
    ${ItemEligibility.fragments.card}
    ${CardScore.fragments.anyCard}
    ${CardBonus.fragments.anyCard}
    ${getCardSpecialEditionBonus.fragments.anyCardInterface}
    ${InjuryTag.fragments.anyPlayer}
  ` as TypedDocumentNode<CardProperties_anyCard>,
  anyCardWithStats: gql`
    fragment CardPropertiesWithStats_anyCard on AnyCardInterface {
      slug
      ...TokenStatsIcon_anyCard
    }
    ${TokenStatsIcon.fragments.anyCard}
  ` as TypedDocumentNode<CardPropertiesWithStats_anyCard>,
};
